/*
  Solis:
  1. "Add" buttons on Solis product cards add the item without leaving the page.
     Uses the same cart event as the theme's own product forms, so the cart count updates
     and the cart drawer opens. If anything fails, the form falls back to a normal submit.
  2. The phone app bar (as on solisbodyclub.com): shows once the hero buttons scroll away,
     hides at the closing band, keeps its cart count in step and opens the cart drawer.
*/
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const routes = (window.Theme && window.Theme.routes) || {};
const ADD_URL = routes.cart_add_url || '/cart/add';
const CART_URL = routes.cart_url || '/cart';

function showMessage(form, text) {
  let msg = form.querySelector('.sx-buy__msg');
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'sx-buy__msg';
    msg.setAttribute('role', 'status');
    form.append(msg);
  }
  msg.textContent = text;
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form.sx-buy[data-sx-add]')) return;
  event.preventDefault();
  if (form.classList.contains('is-busy')) return;

  const id = form.querySelector('input[name="id"]')?.value;
  if (!id) return form.submit();

  form.classList.add('is-busy');
  form.querySelector('.sx-buy__msg')?.remove();

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  let response;
  try {
    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sectionIds.join(',') }),
    });
    response = await res.json();
  } catch (error) {
    // Network problem before anything was added: use the normal form instead.
    form.classList.remove('is-busy');
    form.submit();
    return;
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const cart = await fetch(`${CART_URL}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const failed = Boolean(response && response.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: failed,
        items: cart.items,
        source: 'solis-card',
        itemCount: 1,
        sections: failed ? undefined : response.sections,
      },
    });
    if (failed) {
      showMessage(form, response.description || response.message || 'This item could not be added. Please try again.');
    } else {
      form.classList.add('is-added');
      setTimeout(() => form.classList.remove('is-added'), 1800);
    }
  } catch (error) {
    deferred.reject(error);
    window.location.href = CART_URL;
  } finally {
    form.classList.remove('is-busy');
  }
});

/* Phone app bar */
const bar = document.querySelector('[data-sx-appbar]');
if (bar) {
  let ticking = false;
  const wrapper = document.querySelector('.page-wrapper');
  const update = () => {
    ticking = false;
    const actions = document.querySelector('[data-sx-hero-actions]');
    const end = document.querySelector('.sx-final');
    const scrolled = Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
    const past = actions ? actions.getBoundingClientRect().bottom < 0 : scrolled > 240;
    const atEnd = end ? end.getBoundingClientRect().top < window.innerHeight * 0.8 : false;
    bar.classList.toggle('is-on', past && !atEnd);
  };
  const request = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };
  document.addEventListener('scroll', request, { passive: true, capture: true });
  window.addEventListener('resize', request);
  update();

  const count = bar.querySelector('[data-sx-count]');
  const setCount = (n) => {
    if (!count || typeof n !== 'number') return;
    count.textContent = String(n);
    count.hidden = n < 1;
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ cart }) => setCount(cart?.totalQuantity))
      .catch(() => {});
  });

  bar.querySelector('[data-sx-cart]')?.addEventListener('click', (event) => {
    // The cart drawer sits inside the theme's <theme-drawer>, which opens it. Without one, the link goes to /cart.
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open();
    }
  });
}

